import { CalendarClock, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { cn, StatusBadge, Text } from "@athena/ui";
import { CHANNEL_ICON, CHANNEL_TONE } from "../helpers/channelIcon";
import type { ClientRecord, ContactEntry } from "../helpers/client";
import { alumnusPath, fullName, CONTACT_OUTCOME_VARIANT, formatCalendarDate, formatClockTime } from "../helpers/client";
import { useOpenedFromHere } from "../helpers/openedFrom";

interface ContactLogItemProps {
    client: ClientRecord;
    contact: ContactEntry;
    /** Opens the contact itself: the whole row is its button. */
    onOpen: () => void;
}

/**
 * One contact in the contact log: who, how, what was said, how it went, who logged it, and at what
 * time — the page heads each day's contacts with the date.
 *
 * The whole row opens the contact: a button stretched under it, named for the contact so a screen
 * reader hears which one. The alumnus's name sits above that and still links to their profile.
 * Name (with any follow-up date under it), outcome and chevron always show; the note, time, channel
 * and coordinator join in that order as the row widens, so the note always keeps room.
 */
export function ContactLogItem({ client, contact, onOpen }: ContactLogItemProps) {
    const { t } = useTranslation();
    const openedFrom = useOpenedFromHere();
    const ChannelIcon = CHANNEL_ICON[contact.channel];

    return (
        <div className="@container">
            <div className="border-border focus-within:ring-ring bg-card relative flex items-center gap-4 rounded-2xl border px-4 py-4 transition-shadow focus-within:ring-2 hover:shadow-md @3xl:gap-6 @3xl:px-6 @3xl:py-5">
                <button
                    type="button"
                    className="absolute inset-0 rounded-2xl focus-visible:outline-none"
                    aria-label={t("admin.alumni.contactLog.open", {
                        channel: t(`admin.alumni.channel.${contact.channel}`),
                        name: fullName(client),
                        outcome: t(`admin.alumni.outcome.${contact.outcome}`).toLowerCase(),
                        time: formatClockTime(contact.contactedAt),
                    })}
                    onClick={onOpen}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5 @xl:w-34 @xl:flex-none @5xl:w-52">
                    <Link
                        to={alumnusPath(client.id)}
                        state={openedFrom}
                        className="font-display text-text-heading md:text-md relative z-10 min-w-0 self-start truncate font-semibold hover:underline"
                    >
                        {fullName(client)}
                    </Link>
                    {contact.followUpDate ? (
                        <Text size="xs" muted className="flex min-w-0 items-center gap-1.5 font-mono">
                            <CalendarClock className="h-3 w-3 shrink-0" aria-hidden />
                            <span className="truncate">
                                {t("admin.alumni.history.followUp", {
                                    date: formatCalendarDate(contact.followUpDate),
                                })}
                            </span>
                        </Text>
                    ) : null}
                </div>
                <div className="hidden w-32 shrink-0 items-center gap-3 @4xl:flex">
                    <span
                        className={cn(
                            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                            CHANNEL_TONE[contact.channel],
                        )}
                    >
                        <ChannelIcon className="h-2.5 w-2.5" aria-hidden />
                    </span>
                    <Text className="text-xs whitespace-nowrap">{t(`admin.alumni.channel.${contact.channel}`)}</Text>
                </div>
                <Text
                    size="xs"
                    className={cn("hidden min-w-0 flex-1 truncate @xl:block", !contact.note && "text-text-caption")}
                >
                    {contact.note ?? t("admin.alumni.contactLog.noNote")}
                </Text>
                <StatusBadge
                    size="xs"
                    variant={CONTACT_OUTCOME_VARIANT[contact.outcome]}
                    className="ml-auto shrink-0 border-transparent lowercase @xl:ml-0"
                >
                    {t(`admin.alumni.outcome.${contact.outcome}`)}
                </StatusBadge>
                <div className="hidden shrink-0 items-baseline gap-6 @3xl:flex">
                    <Text size="xs" className="text-text-caption hidden w-32 truncate text-right @6xl:block">
                        {contact.contactedBy}
                    </Text>
                    <Text size="xs" muted className="w-16 text-right font-mono whitespace-nowrap">
                        <time dateTime={contact.contactedAt}>{formatClockTime(contact.contactedAt)}</time>
                    </Text>
                </div>
                <ChevronRight className="text-text-caption h-4 w-4 shrink-0" aria-hidden />
            </div>
        </div>
    );
}
